import { isNotFound, Link, useRouteContext } from '@tanstack/react-router';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { usePortalCampaignQuery } from '@/lib/queries-portal';
import { Button } from '@worldcoo/ui/components/button';
import { useLingui } from '@lingui/react/macro';
import { Gradient } from '@worldcoo/ui/custom/gradient';
import { type ReactNode, Suspense } from 'react';
import { Spinner } from '@worldcoo/ui/components/spinner';
import { useCampaignDisplayContext } from './campaign-display-context';

export function MobileDonateButton(props: { campaignId: string }) {
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		return <DisplayContextMobileDonateButton />;
	}
	return (
		<MobileDonateButtonShell
			button={
				<Suspense
					fallback={
						<Button size="2xl" className="w-full">
							<Spinner />
						</Button>
					}
				>
					<SuspensedMobileDonateButton campaignId={props.campaignId} />
				</Suspense>
			}
		/>
	);
}

function DisplayContextMobileDonateButton() {
	const { campaign, onDonate } = useCampaignDisplayContext()!;
	const { t } = useLingui();
	return (
		<MobileDonateButtonShell
			button={
				campaign.finished ? (
					<Button size="2xl" className="w-full" disabled>
						{t`Campaign funded`}
					</Button>
				) : (
					<Button size="2xl" className="w-full" onClick={onDonate}>
						{t`Make donation`}
					</Button>
				)
			}
		/>
	);
}

function MobileDonateButtonShell(props: { button: ReactNode }) {
	return (
		<DivWithPrimaryColorVariable className="sticky bottom-0 block p-6 lg:hidden">
			<Gradient className="absolute -z-10" />
			<div className="bg-background rounded-[12px]">{props.button}</div>
		</DivWithPrimaryColorVariable>
	);
}

function SuspensedMobileDonateButton(props: { campaignId: string }) {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;

	if (campaign.data.finished || campaign.data.disabled) {
		return (
			<Button size="2xl" className="w-full" disabled>
				{t`Campaign funded`}
			</Button>
		);
	}

	return (
		<Button size="2xl" className="w-full" asChild>
			<Link
				to="/$portalId/$campaignId/donate"
				params={{ portalId, campaignId: props.campaignId }}
				search={(prev) => prev}
			>
				{t`Make donation`}
			</Link>
		</Button>
	);
}
